import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, ExternalLink, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { getIntroductionContent, isContentAvailable } from '../content/introductionContent';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function IntroductionPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [hasRead, setHasRead] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const content = getIntroductionContent(language);
  const contentAvailable = isContentAvailable(language);

  const handleAcknowledge = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/introduction-acknowledged`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ language, acknowledged_at: new Date().toISOString() })
      });

      if (response.ok) {
        setIsAcknowledged(true);
        toast.success('Introduction acknowledged!');
      }
    } catch (error) {
      console.error('Error acknowledging:', error);
    }
  };

  // Chrome will pop-up-block window.open() in some contexts even from a click
  // handler.  Use a real <a target="_blank"> below and only mark "Read"
  // through a separate onClick on the link itself.

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader 
        title="Introduction to ARP"
        parentSection={{ label: 'Self-Work', route: '/self-work' }}
      />

      <main className="pt-24 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          {/* Week Badge */}
          <div className="inline-block bg-amber-400 text-amber-900 px-4 py-2 rounded-lg mb-4">
            <p className="text-xs font-black uppercase tracking-wider">{t('dashboard.twoWeekWindow')}</p>
          </div>

          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {content.title}
          </h1>
          <p className="text-sm text-blue-100 mb-6">{content.subtitle}</p>

          {/* Language Notice */}
          {!contentAvailable && (
            <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Content not yet available in {content.language}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Showing English version. Translation coming soon.
                </p>
              </div>
            </div>
          )}

          {/* PDF Card */}
          <div className="bg-white rounded-xl overflow-hidden shadow-lg mb-6" data-testid="introduction-pdf-card">
            <div className="bg-blue-50 p-6 flex items-center gap-4">
              <div className="p-4 bg-blue-100 rounded-xl">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-gray-800">Introduction Document</h2>
                <p className="text-sm text-gray-500">{content.language} Version • PDF</p>
              </div>
              {hasRead && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-xs font-medium">Read</span>
                </div>
              )}
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                This document introduces you to the Axiom Recovery Programme (ARP). 
                It covers the programme structure, the H.O.W. principles (Honesty, Open-Mindedness, Willingness), 
                and the three-fold nature of addiction (Physical, Mental, Emotional).
              </p>

              <div className="flex gap-3">
                <a
                  href={content.pdfUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { if (content.pdfUrl) setHasRead(true); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md h-10 px-4 text-sm font-medium transition-colors"
                  data-testid="read-introduction-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Read Document
                </a>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (content.pdfUrl) {
                      const link = document.createElement('a');
                      link.href = content.pdfUrl;
                      link.download = `Axiom_Introduction_${content.language}.pdf`;
                      link.click();
                    }
                  }}
                  className="border-blue-200 text-blue-600 hover:bg-blue-50"
                  data-testid="download-introduction-btn"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Key Points Summary */}
          <div className="bg-white rounded-xl p-6 mb-6" data-testid="key-points">
            <h3 className="font-bold text-gray-800 uppercase tracking-wide text-sm mb-4">Key Points</h3>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-sm">H</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Honesty</p>
                  <p className="text-sm text-gray-600">Be honest with yourself about the extent of your use and the damage it has caused.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-sm">O</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Open-Mindedness</p>
                  <p className="text-sm text-gray-600">Drop your existing ideas about Recovery and be willing to absorb new information.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-sm">W</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Willingness</p>
                  <p className="text-sm text-gray-600">Be willing to do whatever is necessary to stay well, regardless of discomfort.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Acknowledge Button */}
          {!isAcknowledged ? (
            <Button
              onClick={handleAcknowledge}
              disabled={!hasRead}
              className={`w-full h-14 rounded-xl font-bold text-sm uppercase tracking-wide shadow-lg ${
                hasRead 
                  ? 'bg-white text-blue-700 hover:bg-blue-50' 
                  : 'bg-white/50 text-blue-400 cursor-not-allowed'
              }`}
              data-testid="acknowledge-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {hasRead ? 'I Have Read & Understood' : 'Read Document First'}
            </Button>
          ) : (
            <div className="bg-green-100 border border-green-300 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-bold text-green-800">Introduction Acknowledged</p>
                <p className="text-sm text-green-700">You can proceed with the programme</p>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default IntroductionPage;
